import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ArrowBack, CheckCircle, Send } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  paper: {
    backgroundColor: "#0f1117",
    color: "#fff",
    borderRadius: 16,
  },
  infoBox: {
    backgroundColor: "rgba(34, 158, 217, 0.08)",
    border: "1px solid rgba(34, 158, 217, 0.25)",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  successBox: {
    backgroundColor: "rgba(76, 175, 80, 0.08)",
    border: "1px solid rgba(76, 175, 80, 0.3)",
    borderRadius: 8,
    padding: theme.spacing(2),
    textAlign: "center",
  },
  input: {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
      "&:hover fieldset": { borderColor: "rgba(34, 158, 217, 0.5)" },
      "&.Mui-focused fieldset": { borderColor: "#229ED9" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
  },
}));

function TelegramBotModal({ open, onClose, onBack, onCompleted }) {
  const classes = useStyles();
  const [phase, setPhase] = useState("form");
  const [botToken, setBotToken] = useState("");
  const [botName, setBotName] = useState("");
  const [errorText, setErrorText] = useState("");
  const [botInfo, setBotInfo] = useState(null);

  const reset = useCallback(() => {
    setPhase("form");
    setBotToken("");
    setBotName("");
    setErrorText("");
    setBotInfo(null);
  }, []);

  const handleValidateAndConnect = async () => {
    if (!botToken.trim()) {
      toast.warning(i18n.t("telegramBot.tokenRequired"));
      return;
    }

    const tokenPattern = /^\d+:[A-Za-z0-9_-]{35,}$/;
    if (!tokenPattern.test(botToken.trim())) {
      toast.error(i18n.t("telegramBot.invalidToken"));
      return;
    }

    setPhase("validating");
    setErrorText("");

    try {
      const { data } = await api.post("/hub/telegram-connect", {
        botToken: botToken.trim(),
        name: botName.trim() || undefined,
      });

      setBotInfo(data);
      setPhase("success");
      toast.success(i18n.t("telegramBot.success"));
      if (onCompleted) onCompleted();
    } catch (e) {
      setErrorText(e.response?.data?.error || i18n.t("telegramBot.errors.connectFail"));
      setPhase("form");
      toast.error(e.response?.data?.error || i18n.t("telegramBot.errors.connectFail"));
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={() => { reset(); onClose(); }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle disableTypography>
        <Box display="flex" alignItems="center">
          {onBack && (
            <IconButton edge="start" onClick={onBack} style={{ color: "#fff", marginRight: 8 }}>
              <ArrowBack />
            </IconButton>
          )}
          <Send style={{ color: "#229ED9", marginRight: 8, fontSize: 28 }} />
          <Typography variant="h6" style={{ color: "#fff", fontWeight: 700 }}>
            {i18n.t("telegramBot.title")}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {phase === "form" && (
          <Box>
            <Box className={classes.infoBox}>
              <Typography variant="body2" style={{ color: "rgba(255,255,255,0.8)" }} gutterBottom>
                {i18n.t("telegramBot.instructions")}
              </Typography>
              <Typography variant="caption" style={{ color: "rgba(255,255,255,0.5)" }}>
                {i18n.t("telegramBot.instructionsDetail")}
              </Typography>
            </Box>

            <TextField
              fullWidth
              label={i18n.t("telegramBot.nameLabel")}
              variant="outlined"
              margin="normal"
              value={botName}
              onChange={(e) => setBotName(e.target.value)}
              placeholder={i18n.t("telegramBot.namePlaceholder")}
              className={classes.input}
            />

            <TextField
              fullWidth
              label={i18n.t("telegramBot.tokenLabel")}
              variant="outlined"
              margin="normal"
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder="123456789:ABCDefGhIJKlmnOPQrstUVwxyz123456789"
              className={classes.input}
              helperText={errorText || i18n.t("telegramBot.tokenHelp")}
              error={Boolean(errorText)}
            />

            <Box mt={2} display="flex" justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={handleValidateAndConnect}
                style={{
                  backgroundColor: "#229ED9",
                  color: "#fff",
                  fontWeight: 700,
                  padding: "12px 32px",
                  borderRadius: 8,
                  textTransform: "none",
                }}
              >
                {i18n.t("telegramBot.btnConnect")}
              </Button>
            </Box>
          </Box>
        )}

        {phase === "validating" && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CircularProgress style={{ color: "#229ED9" }} />
            <Typography variant="body2" style={{ color: "rgba(255,255,255,0.7)", marginTop: 16 }}>
              {i18n.t("telegramBot.validating")}
            </Typography>
          </Box>
        )}

        {phase === "success" && (
          <Box className={classes.successBox}>
            <CheckCircle style={{ fontSize: 48, color: "#4caf50", marginBottom: 8 }} />
            <Typography variant="h6" style={{ color: "#fff", fontWeight: 700 }}>
              {i18n.t("telegramBot.successTitle")}
            </Typography>
            {botInfo && (
              <Typography variant="body2" style={{ color: "rgba(255,255,255,0.7)", marginTop: 8 }}>
                Bot: @{botInfo.botUsername || botInfo.username}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: 16 }}>
        <Button
          onClick={() => { reset(); onClose(); }}
          style={{ color: "rgba(255,255,255,0.7)" }}
        >
          {phase === "success" ? i18n.t("telegramBot.done") : i18n.t("telegramBot.cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TelegramBotModal;
